<template>
  <material-card
    icon="mdi-qrcode-scan"
    icon-small
    color="primary"
    class="kiosk-shell"
    title="Android Kiosk QR Provisioning"
    subtitle="Generate an Android Enterprise QR payload for kiosk app enrollment."
  >
    <v-card-text class="kiosk-shell__body">
      <v-row
        dense
        align="stretch"
        class="provisioning-grid"
      >
        <v-col cols="12">
          <v-alert
            dense
            outlined
            border="left"
            type="info"
            class="mb-5 provisioning-note"
          >
            Use Upload APK to generate the checksum locally and fill the download URL, or choose an existing APK from Scripts Downloads to reuse its URL. Existing APKs are downloaded and hashed in the browser.
          </v-alert>
        </v-col>

        <v-col
          cols="12"
          lg="7"
        >
          <v-card
            outlined
            class="provisioning-panel section-card"
          >
            <v-card-title class="section-card__title">
              <div class="section-card__title-content">
                <div>
                  <div class="text-subtitle-1 font-weight-medium">
                    Provisioning Details
                  </div>
                  <div class="text-caption grey--text text--darken-1 section-card__subtext">
                    Configure the APK, device admin receiver, and optional network details.
                  </div>
                </div>
                <div class="section-card__title-actions">
                  <input
                    ref="apkProvisioningFileInput"
                    type="file"
                    accept=".apk,application/vnd.android.package-archive,application/octet-stream"
                    class="d-none"
                    @change="onProvisioningApkFileSelected"
                  >
                  <app-btn
                    class="section-upload-btn"
                    :disabled="isProvisioningApkBusy"
                    @click="openProvisioningApkFilePicker"
                  >
                    <v-icon
                      v-if="!isProvisioningApkBusy"
                      class="mr-1"
                    >
                      mdi-cloud-upload-outline
                    </v-icon>
                    {{ provisioningUploadButtonText }}
                  </app-btn>

                  <v-menu
                    v-model="existingApkMenu"
                    offset-y
                    left
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        outlined
                        color="primary"
                        class="section-select-btn"
                        :loading="isLoadingExistingApks || isDownloadingExistingApk"
                        :disabled="isProvisioningApkBusy"
                        v-bind="attrs"
                        v-on="on"
                        @click="openExistingApkMenu"
                      >
                        <v-icon
                          left
                          small
                        >
                          mdi-folder-download-outline
                        </v-icon>
                        Existing APKs
                        <v-icon
                          right
                          small
                        >
                          mdi-menu-down
                        </v-icon>
                      </v-btn>
                    </template>

                    <v-list
                      dense
                      style="min-width: 340px; max-width: 420px; max-height: 360px; overflow-y: auto"
                    >
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            Scripts Downloads
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            Select an APK already uploaded in Scripts Downloads to fill the URL and calculate the checksum locally.
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-btn
                          icon
                          small
                          :disabled="isLoadingExistingApks"
                          @click.stop="reloadExistingApkOptions"
                        >
                          <v-icon small>
                            mdi-refresh
                          </v-icon>
                        </v-btn>
                      </v-list-item>

                      <v-divider />

                      <v-list-item
                        class="existing-apk-filter"
                        @click.stop="showOnlyLatestExistingApk = !showOnlyLatestExistingApk"
                      >
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            Latest APK only
                          </v-list-item-title>
                        </v-list-item-content>
                        <v-switch
                          v-model="showOnlyLatestExistingApk"
                          dense
                          inset
                          hide-details
                          class="ma-0"
                          @click.stop
                        />
                      </v-list-item>

                      <v-divider />

                      <v-list-item v-if="isLoadingExistingApks">
                        <v-list-item-content>
                          <v-list-item-title>Loading uploaded APKs...</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-else-if="existingApkLoadError">
                        <v-list-item-content>
                          <v-list-item-title class="error--text">
                            {{ existingApkLoadError }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-else-if="!displayedExistingApkOptions.length">
                        <v-list-item-content>
                          <v-list-item-title>No MyDevice APK files found.</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>

                      <template v-else>
                        <v-list-item
                          v-for="item in displayedExistingApkOptions"
                          :key="item.id"
                          @click="selectExistingApk(item)"
                        >
                          <v-list-item-content>
                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.filePathLabel }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="section-card__body">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model.trim="form.apkDownloadUrl"
                    outlined
                    label="APK Download URL"
                    placeholder="https://example.com/mydevice.apk"
                    hint="Required. Use the direct download URL Android should install from."
                    persistent-hint
                    :error-messages="fieldErrors.apkDownloadUrl"
                    @blur="touchField('apkDownloadUrl')"
                  />
                  <v-progress-linear
                    v-if="isUploadingDownloadUrl"
                    :value="downloadUrlUploadProgress"
                    height="6"
                    rounded
                    color="primary"
                    class="mb-2 mt-n1"
                  />
                  <div
                    v-if="downloadUrlUploadError"
                    class="text-caption error--text mt-n2 mb-1"
                  >
                    {{ downloadUrlUploadError }}
                  </div>
                  <div class="text-caption grey--text text--darken-1 mt-n1 mb-4">
                    {{ downloadUrlSourceMessage }}
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model.trim="form.apkChecksum"
                    outlined
                    label="APK SHA-256 Checksum"
                    hint="Required. The checksum is stored as URL-safe Base64 with no trailing equals sign."
                    persistent-hint
                    :error-messages="fieldErrors.apkChecksum"
                    @blur="touchField('apkChecksum'); normalizeChecksumField()"
                  />
                  <div
                    v-if="apkUploadError"
                    class="text-caption error--text mt-n2 mb-1"
                  >
                    {{ apkUploadError }}
                  </div>
                  <v-progress-linear
                    v-if="isDownloadingExistingApk"
                    :value="existingApkDownloadProgress"
                    :indeterminate="!isExistingApkDownloadProgressKnown"
                    height="6"
                    rounded
                    color="primary"
                    class="mb-2 mt-n1"
                  />
                  <div class="text-caption grey--text text--darken-1 mt-n1 mb-4">
                    {{ checksumSourceMessage }}
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model.trim="form.adminComponentName"
                    outlined
                    label="Admin Component Name"
                    :error-messages="fieldErrors.adminComponentName"
                    @blur="touchField('adminComponentName')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-divider class="mb-4" />
                  <div class="text-subtitle-2 font-weight-medium mb-3">
                    Admin Extras Bundle
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-combobox
                    v-model="form.deviceImei"
                    outlined
                    label="Vehicle Number"
                    :items="vehicleNumberOptions"
                    item-text="name"
                    item-value="name"
                    :search-input.sync="vehicleSearchInput"
                    :loading="isLoadingCompanyDevices"
                    :filter="filterVehicleNumberOption"
                    hide-no-data
                    clearable
                    hint="Required. Use uppercase letters first, followed by numbers only. Example: T7518."
                    persistent-hint
                    :error-messages="fieldErrors.deviceImei"
                    @change="onVehicleNumberChanged"
                    @blur="touchField('deviceImei'); applyVehicleMacForCurrentInput()"
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model.trim="form.deviceMacAddress"
                    outlined
                    label="MAC Address"
                    :hint="deviceMacAddressHint"
                    persistent-hint
                    :readonly="!!selectedVehicleMacAddress"
                    :append-icon="selectedVehicleMacAddress ? '' : 'mdi-refresh'"
                    :error-messages="fieldErrors.deviceMacAddress"
                    @change="onDeviceMacAddressChanged"
                    @click:append="regenerateDeviceMacAddress"
                    @blur="touchField('deviceMacAddress')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-divider class="mb-4" />
                  <div class="wifi-section-heading mb-3">
                    <div>
                      <div class="text-subtitle-2 font-weight-medium">
                        Optional Wi-Fi
                      </div>
                      <div
                        class="text-caption"
                        :class="wifiConfigLoadError ? 'error--text' : 'grey--text text--darken-1'"
                      >
                        {{ wifiConfigStatusMessage }}
                      </div>
                    </div>
                    <v-btn
                      icon
                      small
                      :loading="isLoadingWifiConfig"
                      @click="reloadCompanyWifiConfig"
                    >
                      <v-icon small>
                        mdi-refresh
                      </v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-select
                    v-if="hasMultipleWifiConfigs"
                    v-model="selectedWifiConfigKey"
                    outlined
                    label="WiFi SSID"
                    :items="wifiConfigSelectItems"
                    item-text="label"
                    item-value="value"
                    hint="Choose which company Wi-Fi config to include in the QR payload."
                    persistent-hint
                    @change="selectWifiConfigByKey"
                  />
                  <v-text-field
                    v-else
                    v-model.trim="form.wifiSsid"
                    outlined
                    label="WiFi SSID"
                    hint="Leave blank to omit Wi-Fi settings from the QR payload."
                    persistent-hint
                  />
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-combobox
                    v-model="form.wifiSecurityType"
                    outlined
                    label="WiFi Security Type"
                    :items="wifiSecurityTypes"
                    hint="Included in the QR only when a WiFi SSID is provided."
                    persistent-hint
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="form.wifiPassword"
                    outlined
                    label="WiFi Password"
                    :type="showWifiPassword ? 'text' : 'password'"
                    :append-icon="showWifiPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    hint="You can enter this before the SSID. It is only included when WiFi SSID is filled."
                    persistent-hint
                    @click:append="toggleWifiPassword"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col
          cols="12"
          lg="5"
        >
          <v-card
            outlined
            class="provisioning-panel section-card section-card--qr"
          >
            <v-card-title class="section-card__title">
              <div>
                <div class="text-subtitle-1 font-weight-medium">
                  Provisioning QR Code
                </div>
                <div class="text-caption grey--text text--darken-1">
                  Scan this QR during Android Enterprise enrollment.
                </div>
              </div>
            </v-card-title>

            <v-card-text class="section-card__body section-card__body--qr">
              <v-alert
                v-if="validationSummary.length"
                dense
                outlined
                border="left"
                type="warning"
                class="mb-3 qr-warning"
              >
                {{ validationSummaryText }}
              </v-alert>

              <div
                ref="qrCanvasWrapper"
                class="qr-preview"
                :class="{ 'qr-preview--empty': !canGenerateProvisioning }"
              >
                <div
                  v-if="canGenerateProvisioning"
                  class="qr-preview__frame"
                >
                  <qrcode-vue
                    :value="qrValue"
                    :size="$vuetify.breakpoint.mdAndDown ? 360 : 460"
                    level="L"
                    render-as="canvas"
                  />
                </div>

                <div
                  v-else
                  class="text-body-2 text-center grey--text text--darken-1"
                >
                  The QR code will appear here once the required fields are complete.
                </div>
              </div>

              <div class="qr-actions">
                <app-btn
                  class="qr-action"
                  :disabled="!canGenerateProvisioning"
                  @click="copyJson"
                >
                  <v-icon class="mr-1">
                    mdi-content-copy
                  </v-icon>
                  Copy JSON
                </app-btn>

                <app-btn
                  class="qr-action"
                  :disabled="!canGenerateProvisioning"
                  @click="downloadQrCode"
                >
                  <v-icon class="mr-1">
                    mdi-download
                  </v-icon>
                  Download QR Code
                </app-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </material-card>
</template>

<script>
  import formatter from '@/util/formatter'
  import {
    computed,
    defineComponent,
    getCurrentInstance,
    nextTick,
    reactive,
    ref,
    watch,
  } from '@vue/composition-api'
  import QrcodeVue from 'qrcode.vue'
  import { uuidv7 } from 'uuidv7'
  import { encryptCompanyCode } from '@/utils/companyCodeCipher'

  const DEFAULTS = {
    adminComponentName:
      'au.com.softclient.mydevices/.service.device.MyDeviceAdminReceiver',
    wifiSsid: '',
    wifiPassword: '',
    wifiSecurityType: 'WPA',
    // locale: 'en_AU',
    // timeZone: 'Australia/Sydney',
    // skipEncryption: true,
  }

  const WIFI_SECURITY_TYPES = [
    'WPA',
    'WPA2',
    'WPA3',
    'WPA/WPA2 PSK',
    'WPA2/WPA3 PSK',
    'SAE',
    'OWE',
    'EAP',
    'EAP-TLS',
    'EAP-TTLS',
    'PEAP',
    'WEP',
    'NONE',
  ]
  const getNestedStringValue = (candidate) => {
    if (typeof candidate === 'string') {
      return candidate.trim()
    }

    if (candidate && typeof candidate === 'object') {
      const nestedCandidates = [
        candidate.path,
        candidate.filePath,
        candidate.fileName,
        candidate.name,
        candidate.url,
      ]

      return nestedCandidates.find(value => typeof value === 'string' && value.trim())
    }

    return ''
  }

  const PROVISIONING_KEYS = {
    adminComponentName:
      'android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME',
    downloadLocation:
      'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION',
    checksum: 'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM',
    wifiSsid: 'android.app.extra.PROVISIONING_WIFI_SSID',
    wifiPassword: 'android.app.extra.PROVISIONING_WIFI_PASSWORD',
    wifiSecurityType: 'android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE',
    adminExtrasBundle: 'android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE',
    // locale: 'android.app.extra.PROVISIONING_LOCALE',
    // timeZone: 'android.app.extra.PROVISIONING_TIME_ZONE',
    // skipEncryption: 'android.app.extra.PROVISIONING_SKIP_ENCRYPTION',
  }

  const SHA_256_BASE64_URL_PATTERN = /^[A-Za-z0-9_-]{43}$/
  const VEHICLE_NUMBER_PATTERN = /^[A-Z]+[0-9]*$/
  const MYDEVICE_PACKAGE_ID = 'au.com.softclient.mydevices'
  const MYDEVICE_APK_PATTERN = /(?:^|[/\\_\s-])mydevice(?:[/\\_\s.-]|$)/i

  const generateUuidV7MacAddress = () => {
    const uuidHex = uuidv7().replace(/-/g, '')
    const macBytes = uuidHex.slice(0, 12).match(/.{1,2}/g) || []

    if (macBytes.length) {
      const firstByte = parseInt(macBytes[0], 16)
      macBytes[0] = ((firstByte | 0x02) & 0xfe).toString(16).padStart(2, '0')
    }

    return macBytes.join(':').toUpperCase()
  }

  const normalizeVehicleKey = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')

  const normalizeMacAddressValue = (value) =>
    String(value || '')
      .trim()
      .toUpperCase()

  const getDeviceNameValue = (device) =>
    String(device?.name || '').trim()

  const getDeviceMacAddressValue = (device) =>
    normalizeMacAddressValue(
      device?.macAddress ||
      device?.deviceMacAddress ||
      device?.deviceId ||
      device?.id ||
      '',
    )

  const getDeviceLatestTimestamp = (device) => {
    const candidates = [
      device?.lastConnected,
      device?.lastConnectedAt,
      device?.updatedAt,
      device?.modifiedAt,
      device?.createdAt,
      device?.created,
    ]

    for (const value of candidates) {
      if (!value) {
        continue
      }

      // Parse as UTC: the API's timestamps are naive, and mixing a naive read
      // with an offset-bearing one would order the candidates wrongly.
      const timestamp = formatter.toDate(value)
      if (timestamp) {
        return timestamp.getTime()
      }
    }

    return 0
  }

  const readFileAsArrayBuffer = (file) => {
    if (typeof file.arrayBuffer === 'function') {
      return file.arrayBuffer()
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsArrayBuffer(file)
    })
  }

  const normalizeChecksumValue = (value) => {
    if (typeof value !== 'string') {
      return value
    }

    return value
      .trim()
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '')
  }

  const arrayBufferToBase64Url = (buffer) => {
    const bytes = new Uint8Array(buffer)
    const chunkSize = 0x8000
    let binary = ''

    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, index + chunkSize)
      binary += String.fromCharCode.apply(null, chunk)
    }

    return normalizeChecksumValue(window.btoa(binary))
  }

  const copyTextToClipboard = async (value) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(value)
      return
    }

    const fallback = document.createElement('textarea')
    fallback.value = value
    fallback.setAttribute('readonly', '')
    fallback.style.position = 'absolute'
    fallback.style.left = '-9999px'
    document.body.appendChild(fallback)
    fallback.select()
    document.execCommand('copy')
    document.body.removeChild(fallback)
  }

  export default defineComponent({
    name: 'KioskQrProvisioning',

    components: {
      QrcodeVue,
    },

    mounted () {
      this.showOnlyLatestExistingApk = true
    },

    activated () {
      this.showOnlyLatestExistingApk = true
    },

    setup () {
      const { proxy } = getCurrentInstance()
      const form = reactive({
        wifiSsid: DEFAULTS.wifiSsid,
        wifiPassword: DEFAULTS.wifiPassword,
        wifiSecurityType: DEFAULTS.wifiSecurityType,
        apkDownloadUrl: '',
        apkChecksum: '',
        adminComponentName: DEFAULTS.adminComponentName,
        deviceImei: '',
        deviceMacAddress: generateUuidV7MacAddress(),
        // locale: DEFAULTS.locale,
        // timeZone: DEFAULTS.timeZone,
        // skipEncryption: DEFAULTS.skipEncryption,
      })
      const touchedFields = reactive({
        apkDownloadUrl: false,
        apkChecksum: false,
        adminComponentName: false,
        deviceImei: false,
        deviceMacAddress: false,
      })
      const uploadedApk = ref(null)
      const uploadedDownloadApkName = ref('')
      const apkUploadError = ref('')
      const downloadUrlUploadError = ref('')
      const existingApkMenu = ref(false)
      const existingApkOptions = ref([])
      const existingApkLoadError = ref('')
      const existingApkOptionsCompanyId = ref(0)
      const showOnlyLatestExistingApk = ref(true)
      const isCalculatingChecksum = ref(false)
      const isDownloadingExistingApk = ref(false)
      const isUploadingDownloadUrl = ref(false)
      const isExistingApkDownloadProgressKnown = ref(false)
      const isLoadingExistingApks = ref(false)
      const isLoadingWifiConfig = ref(false)
      const existingApkDownloadProgress = ref(0)
      const downloadUrlUploadProgress = ref(0)
      const wifiConfigLoadError = ref('')
      const wifiConfigLoadedCompanyId = ref(0)
      const wifiConfigOptions = ref([])
      const wifiConfigRequestToken = ref(0)
      const selectedWifiConfigKey = ref('')
      const showWifiPassword = ref(false)
      const isLoadingCompanyDevices = ref(false)
      const companyDeviceLoadError = ref('')
      const companyDevicesLoadedCompanyId = ref(0)
      const companyDeviceRequestToken = ref(0)
      const companyDeviceRows = ref([])
      const vehicleSearchInput = ref('')
      const selectedExistingVehicleName = ref('')
      const generatedVehicleName = ref('')
      const apkProvisioningFileInput = ref(null)
      const qrCanvasWrapper = ref(null)
      const lastRecordedQrSignature = ref('')
      const qrProvisioningId = ref('')
      const selectedCompanyId = computed(
        () => proxy.$store?.state?.company?.selectedCompanyId || 0,
      )
      const selectedCompanyPath = computed(() =>
        Array.isArray(proxy.$store?.state?.company?.selectedCompanyPath)
          ? proxy.$store.state.company.selectedCompanyPath
          : [],
      )
      const normalizeValue = (value) => (typeof value === 'string' ? value.trim() : value)
      const selectedCompany = computed(() =>
        proxy.$store?.state?.company?.companies?.[selectedCompanyId.value] || null,
      )
      const selectedCompanyCode = computed(() =>
        normalizeValue(selectedCompany.value?.code) || '',
      )
      const baseConfigCompanyId = computed(
        () => proxy.$store?.state?.configs?.baseConfigCompanyId || 1,
      )

      const hasWifiConfiguration = computed(() => normalizeValue(form.wifiSsid).length > 0)

      const hasMultipleWifiConfigs = computed(() => wifiConfigOptions.value.length > 1)

      const wifiConfigSelectItems = computed(() =>
        wifiConfigOptions.value.map((wifiConfig) => {
          const essid = normalizeValue(wifiConfig?.essid) || 'Unnamed Wi-Fi'
          const suffixParts = []

          if (wifiConfig?.active === true) {
            suffixParts.push('active')
          }

          if (wifiConfig?.encryption) {
            suffixParts.push(wifiConfig.encryption)
          }

          return {
            label: suffixParts.length ? `${essid} (${suffixParts.join(', ')})` : essid,
            value: wifiConfig.selectionKey,
          }
        }),
      )

      const wifiConfigStatusMessage = computed(() => {
        if (isLoadingWifiConfig.value) {
          return 'Loading company Wi-Fi config...'
        }

        if (wifiConfigLoadError.value) {
          return wifiConfigLoadError.value
        }

        if (hasMultipleWifiConfigs.value) {
          return 'Choose a company Wi-Fi config for this QR payload.'
        }

        if (wifiConfigLoadedCompanyId.value && hasWifiConfiguration.value) {
          return 'Loaded from the selected company Wi-Fi config.'
        }

        return 'No company Wi-Fi config is loaded.'
      })

      const vehicleNumberOptions = computed(() => {
        const latestByVehicle = {}

        companyDeviceRows.value.forEach((device, index) => {
          const name = getDeviceNameValue(device)
          const macAddress = getDeviceMacAddressValue(device)
          const key = normalizeVehicleKey(name)

          if (!key || !macAddress) {
            return
          }

          const timestamp = getDeviceLatestTimestamp(device)
          const existing = latestByVehicle[key]

          if (!existing || timestamp > existing.timestamp || (timestamp === existing.timestamp && index > existing.index)) {
            latestByVehicle[key] = {
              name,
              macAddress,
              timestamp,
              index,
            }
          }
        })

        return Object.values(latestByVehicle)
          .sort((left, right) => left.name.localeCompare(right.name))
      })

      const vehicleMacByName = computed(() =>
        vehicleNumberOptions.value.reduce((lookup, option) => {
          lookup[normalizeVehicleKey(option.name)] = option.macAddress
          return lookup
        }, {}),
      )

      const selectedVehicleMacAddress = computed(() =>
        vehicleMacByName.value[normalizeVehicleKey(form.deviceImei)] || '',
      )

      const deviceMacAddressHint = computed(() => {
        if (selectedVehicleMacAddress.value) {
          return 'Reused from the latest matching vehicle in the selected company.'
        }

        if (isLoadingCompanyDevices.value) {
          return 'Loading company device list for vehicle matching...'
        }

        if (companyDeviceLoadError.value) {
          return companyDeviceLoadError.value
        }

        return 'Auto-generated for new vehicles from a date-based UUID v7 value.'
      })

      const baseValidationErrors = computed(() => {
        const errors = {
          apkDownloadUrl: [],
          apkChecksum: [],
          adminComponentName: [],
          deviceImei: [],
          deviceMacAddress: [],
        }
        const trimmedDownloadUrl = normalizeValue(form.apkDownloadUrl)
        const trimmedChecksum = normalizeValue(form.apkChecksum)
        const trimmedAdminComponentName = normalizeValue(form.adminComponentName)
        const trimmedVehicleNumber = normalizeValue(form.deviceImei)
        const trimmedDeviceMacAddress = normalizeValue(form.deviceMacAddress)

        if (!trimmedDownloadUrl) {
          errors.apkDownloadUrl.push('APK download URL is required.')
        } else {
          try {
            const parsedUrl = new URL(trimmedDownloadUrl)
            if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
              errors.apkDownloadUrl.push('Use an http or https URL for the APK download.')
            }
          } catch (error) {
            errors.apkDownloadUrl.push('Enter a valid APK download URL.')
          }
        }

        if (!trimmedChecksum) {
          errors.apkChecksum.push('APK SHA-256 checksum is required.')
        } else if (
          !SHA_256_BASE64_URL_PATTERN.test(normalizeChecksumValue(trimmedChecksum))
        ) {
          errors.apkChecksum.push(
            'Enter a SHA-256 checksum in URL-safe Base64 format, or paste standard Base64 and it will be normalized automatically.',
          )
        }

        if (!trimmedAdminComponentName) {
          errors.adminComponentName.push('Admin component name is required.')
        }

        if (!trimmedVehicleNumber) {
          errors.deviceImei.push('Vehicle number is required.')
        } else if (!VEHICLE_NUMBER_PATTERN.test(trimmedVehicleNumber)) {
          errors.deviceImei.push(
            'Use uppercase letters first, then numbers only. No spaces, symbols, lowercase letters, or letters after numbers.',
          )
        }

        if (!trimmedDeviceMacAddress) {
          errors.deviceMacAddress.push('MAC address is required.')
        }

        return errors
      })

      const isProvisioningApkBusy = computed(
        () =>
          isDownloadingExistingApk.value ||
          isCalculatingChecksum.value ||
          isUploadingDownloadUrl.value,
      )

      const hasBaseValidationErrors = computed(() =>
        Object.values(baseValidationErrors.value).some(errors => errors.length > 0),
      )

      const qrProvisioningSignature = computed(() => {
        const companyId = selectedCompanyId.value
        const deviceName = normalizeVehicleInput(form.deviceImei)
        const deviceCode = normalizeValue(form.deviceMacAddress)

        if (!companyId || !deviceName || !deviceCode) {
          return ''
        }

        return `${companyId}|${normalizeVehicleKey(deviceName)}|${deviceCode.toUpperCase()}`
      })

      const visibleValidationErrors = computed(() => ({
        apkDownloadUrl: isProvisioningApkBusy.value
          ? []
          : baseValidationErrors.value.apkDownloadUrl,
        apkChecksum: isProvisioningApkBusy.value
          ? []
          : baseValidationErrors.value.apkChecksum,
        adminComponentName: baseValidationErrors.value.adminComponentName,
        deviceImei: baseValidationErrors.value.deviceImei,
        deviceMacAddress: baseValidationErrors.value.deviceMacAddress,
      }))

      const fieldErrors = computed(() => ({
        apkDownloadUrl: touchedFields.apkDownloadUrl
          ? visibleValidationErrors.value.apkDownloadUrl
          : [],
        apkChecksum: touchedFields.apkChecksum
          ? visibleValidationErrors.value.apkChecksum
          : [],
        adminComponentName: touchedFields.adminComponentName
          ? visibleValidationErrors.value.adminComponentName
          : [],
        deviceImei: touchedFields.deviceImei
          ? visibleValidationErrors.value.deviceImei
          : [],
        deviceMacAddress: touchedFields.deviceMacAddress
          ? visibleValidationErrors.value.deviceMacAddress
          : [],
      }))

      const validationSummary = computed(() => {
        const messages = []
        const validationErrors = visibleValidationErrors.value

        if (validationErrors.apkDownloadUrl.length) {
          messages.push(validationErrors.apkDownloadUrl[0])
        }

        if (validationErrors.apkChecksum.length) {
          messages.push(validationErrors.apkChecksum[0])
        }

        if (validationErrors.adminComponentName.length) {
          messages.push(validationErrors.adminComponentName[0])
        }

        if (validationErrors.deviceImei.length) {
          messages.push(validationErrors.deviceImei[0])
        }

        if (validationErrors.deviceMacAddress.length) {
          messages.push(validationErrors.deviceMacAddress[0])
        }

        return messages
      })

      const canGenerateProvisioning = computed(
        () =>
          !hasBaseValidationErrors.value &&
          !!qrProvisioningId.value &&
          lastRecordedQrSignature.value === qrProvisioningSignature.value,
      )

      const checksumSourceMessage = computed(() => {
        if (isDownloadingExistingApk.value) {
          return isExistingApkDownloadProgressKnown.value && existingApkDownloadProgress.value > 0
            ? `Downloading selected APK for checksum generation... ${existingApkDownloadProgress.value}%`
            : 'Downloading selected APK for checksum generation...'
        }

        if (isCalculatingChecksum.value) {
          return 'Calculating SHA-256 checksum locally...'
        }

        if (uploadedApk.value) {
          return uploadedApk.value.source === 'metadata'
            ? `Checksum metadata loaded from ${uploadedApk.value.name}.`
            : `Checksum generated from ${uploadedApk.value.name} and kept in local browser memory only.`
        }

        return 'Upload an APK to calculate the checksum locally, choose an existing APK to reuse checksum metadata, or paste a checksum and it will be normalized to URL-safe Base64.'
      })

      const downloadUrlSourceMessage = computed(() => {
        if (isUploadingDownloadUrl.value) {
          return downloadUrlUploadProgress.value > 0
            ? `Uploading APK and generating the direct download URL... ${downloadUrlUploadProgress.value}%`
            : 'Uploading APK and generating the direct download URL...'
        }

        if (uploadedDownloadApkName.value) {
          return `Download URL set from ${uploadedDownloadApkName.value}.`
        }

        return 'Upload an APK to auto-fill the download URL, or choose an existing APK from Scripts Downloads.'
      })

      const provisioningUploadButtonText = computed(() => {
        if (isCalculatingChecksum.value) {
          return 'Generating Checksum...'
        }

        if (!isUploadingDownloadUrl.value) {
          return 'Upload APK'
        }

        return downloadUrlUploadProgress.value > 0
          ? `Uploading ${downloadUrlUploadProgress.value}%`
          : 'Uploading...'
      })

      const validationSummaryText = computed(() => {
        const missingFields = []
        const validationErrors = visibleValidationErrors.value

        if (validationErrors.apkDownloadUrl.length) {
          missingFields.push('APK download URL')
        }

        if (validationErrors.apkChecksum.length) {
          missingFields.push('APK SHA-256 checksum')
        }

        if (validationErrors.adminComponentName.length) {
          missingFields.push('Admin component name')
        }

        if (validationErrors.deviceImei.length) {
          missingFields.push('vehicle number')
        }

        if (validationErrors.deviceMacAddress.length) {
          missingFields.push('MAC address')
        }

        return missingFields.length
          ? `Required before QR: ${missingFields.join(', ')}.`
          : ''
      })

      const provisioningPayload = computed(() => {
        if (!canGenerateProvisioning.value) {
          return null
        }

        const payload = {
          // Build the provisioning payload exactly as Android expects it.
          [PROVISIONING_KEYS.adminComponentName]: normalizeValue(
            form.adminComponentName,
          ),
          [PROVISIONING_KEYS.downloadLocation]: normalizeValue(
            form.apkDownloadUrl,
          ),
          [PROVISIONING_KEYS.checksum]: normalizeChecksumValue(form.apkChecksum),
          // [PROVISIONING_KEYS.locale]:
          //   normalizeValue(form.locale) || DEFAULTS.locale,
          // [PROVISIONING_KEYS.timeZone]:
          //   normalizeTimeZoneValue(form.timeZone) || DEFAULTS.timeZone,
          // [PROVISIONING_KEYS.skipEncryption]: form.skipEncryption,
        }

        const adminExtrasBundle = {}
        const companyCode = selectedCompanyCode.value
        const vehicleNumber = normalizeValue(form.deviceImei)
        const deviceMacAddress = normalizeValue(form.deviceMacAddress)

        if (companyCode) {
          adminExtrasBundle.companyCode = encryptCompanyCode(companyCode)
        }

        if (vehicleNumber) {
          adminExtrasBundle.vehicleNumber = vehicleNumber
        }

        if (deviceMacAddress) {
          adminExtrasBundle.macAddress = deviceMacAddress
        }

        if (qrProvisioningId.value) {
          adminExtrasBundle.qrId = qrProvisioningId.value
        }

        if (Object.keys(adminExtrasBundle).length) {
          payload[PROVISIONING_KEYS.adminExtrasBundle] = adminExtrasBundle
        }

        if (hasWifiConfiguration.value) {
          payload[PROVISIONING_KEYS.wifiSsid] = normalizeValue(form.wifiSsid)
          payload[PROVISIONING_KEYS.wifiPassword] = form.wifiPassword || ''
          payload[PROVISIONING_KEYS.wifiSecurityType] =
            form.wifiSecurityType || DEFAULTS.wifiSecurityType
        }

        return payload
      })

      const formattedJson = computed(() =>
        provisioningPayload.value
          ? JSON.stringify(provisioningPayload.value, null, 2)
          : '',
      )

      const qrValue = computed(() => formattedJson.value)

      const toastOptions = { position: 'topRight' }

      const touchField = (field) => {
        if (Object.prototype.hasOwnProperty.call(touchedFields, field)) {
          touchedFields[field] = true
        }
      }

      const touchRequiredFields = () => {
        touchedFields.apkDownloadUrl = true
        touchedFields.apkChecksum = true
        touchedFields.adminComponentName = true
        touchedFields.deviceImei = true
        touchedFields.deviceMacAddress = true
      }

      const normalizeChecksumField = () => {
        form.apkChecksum = normalizeChecksumValue(form.apkChecksum)
      }

      const regenerateDeviceMacAddress = async () => {
        const vehicleNumber = normalizeVehicleInput(form.deviceImei)
        const existingMacAddress = vehicleMacByName.value[normalizeVehicleKey(vehicleNumber)]

        if (existingMacAddress) {
          form.deviceMacAddress = existingMacAddress
          selectedExistingVehicleName.value = vehicleNumber
          generatedVehicleName.value = ''
          touchedFields.deviceMacAddress = true
          await ensureQrProvisioningRecord()
          return
        }

        form.deviceMacAddress = generateUuidV7MacAddress()
        generatedVehicleName.value = vehicleNumber
        selectedExistingVehicleName.value = ''
        touchedFields.deviceMacAddress = true
        await ensureQrProvisioningRecord()
      }

      const normalizeVehicleInput = (value) => {
        if (value && typeof value === 'object') {
          return getDeviceNameValue(value)
        }

        return String(value || '').trim()
      }

      // Resolve free text typed into the vehicle combobox to an existing
      // vehicle: exact match wins, then a prefix match, then a contains match.
      const findMatchingVehicleOption = (value) => {
        const query = normalizeVehicleKey(value)
        if (!query) {
          return null
        }

        const exactMatch = vehicleNumberOptions.value.find(
          option => normalizeVehicleKey(option.name) === query,
        )
        if (exactMatch) {
          return exactMatch
        }

        const prefixMatch = vehicleNumberOptions.value.find(
          option => normalizeVehicleKey(option.name).startsWith(query),
        )
        if (prefixMatch) {
          return prefixMatch
        }

        return (
          vehicleNumberOptions.value.find(
            option => normalizeVehicleKey(option.name).includes(query),
          ) || null
        )
      }

      const applyVehicleMacForCurrentInput = async () => {
        const typedVehicleNumber = normalizeVehicleInput(form.deviceImei)
        // If the typed text matches an existing vehicle, auto-select that
        // vehicle in full instead of keeping the partial text the user typed.
        const matchedOption = findMatchingVehicleOption(typedVehicleNumber)
        const vehicleNumber = matchedOption ? matchedOption.name : typedVehicleNumber
        const vehicleKey = normalizeVehicleKey(vehicleNumber)

        if (form.deviceImei !== vehicleNumber) {
          form.deviceImei = vehicleNumber
        }

        if (!vehicleKey) {
          selectedExistingVehicleName.value = ''
          generatedVehicleName.value = ''
          return
        }

        const existingMacAddress = matchedOption
          ? matchedOption.macAddress
          : vehicleMacByName.value[vehicleKey]

        if (existingMacAddress) {
          form.deviceMacAddress = existingMacAddress
          selectedExistingVehicleName.value = vehicleNumber
          generatedVehicleName.value = ''
          touchedFields.deviceMacAddress = true
          await ensureQrProvisioningRecord()
          return
        }

        if (normalizeVehicleKey(generatedVehicleName.value) !== vehicleKey) {
          form.deviceMacAddress = generateUuidV7MacAddress()
          generatedVehicleName.value = vehicleNumber
        }

        selectedExistingVehicleName.value = ''
        await ensureQrProvisioningRecord()
      }

      const onVehicleNumberChanged = async (value) => {
        form.deviceImei = normalizeVehicleInput(value)
        touchField('deviceImei')
        await applyVehicleMacForCurrentInput()
      }

      const onDeviceMacAddressChanged = async () => {
        touchField('deviceMacAddress')
        await ensureQrProvisioningRecord()
      }

      const filterVehicleNumberOption = (item, queryText, itemText) => {
        const query = normalizeVehicleKey(queryText)
        if (!query) {
          return true
        }

        return normalizeVehicleKey(itemText || item?.name).includes(query)
      }

      const loadCompanyDevices = async () => {
        const companyId = selectedCompanyId.value
        const requestToken = companyDeviceRequestToken.value + 1
        companyDeviceRequestToken.value = requestToken
        companyDeviceLoadError.value = ''

        if (!companyId) {
          companyDeviceRows.value = []
          companyDevicesLoadedCompanyId.value = 0
          companyDeviceLoadError.value = 'Select a company to reuse existing vehicle MAC addresses.'
          return
        }

        isLoadingCompanyDevices.value = true

        try {
          const pageLimit = 500
          const maxPages = 200
          const devices = []

          for (let page = 1; page <= maxPages; page += 1) {
            const response = await proxy.$http.get('Company/DevicePaginationByCompany', {
              params: {
                companyId,
                page,
                limit: pageLimit,
              },
            })

            if (requestToken !== companyDeviceRequestToken.value) {
              return
            }

            const responseData = response?.data
            const pageDevices = Array.isArray(responseData)
              ? responseData
              : Array.isArray(responseData?.data)
                ? responseData.data
                : []

            devices.push(...pageDevices)

            if (pageDevices.length < pageLimit) {
              break
            }
          }

          companyDeviceRows.value = devices
          companyDevicesLoadedCompanyId.value = companyId
          applyVehicleMacForCurrentInput()
        } catch (error) {
          if (requestToken !== companyDeviceRequestToken.value) {
            return
          }

          companyDeviceRows.value = []
          companyDevicesLoadedCompanyId.value = 0
          companyDeviceLoadError.value =
            error?.response?.data?.message ||
            'We could not load company devices for vehicle suggestions.'
          console.error('[KioskQrProvisioning] Failed to load company devices', {
            selectedCompanyId: selectedCompanyId.value,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
        } finally {
          if (requestToken === companyDeviceRequestToken.value) {
            isLoadingCompanyDevices.value = false
          }
        }
      }

      const openProvisioningApkFilePicker = () => {
        if (apkProvisioningFileInput.value && typeof apkProvisioningFileInput.value.click === 'function') {
          apkProvisioningFileInput.value.click()
        }
      }

      const normalizeWifiConfigOption = (wifiConfig, index) => ({
        ...wifiConfig,
        selectionKey: String(
          wifiConfig?.id ||
          wifiConfig?.wifiConfigId ||
          `${wifiConfig?.referenceId || selectedCompanyId.value || 'company'}-${wifiConfig?.essid || 'wifi'}-${index}`,
        ),
      })

      const normalizeWifiConfigResponse = (responseData) => {
        const data = responseData?.data && typeof responseData.data === 'object'
          ? responseData.data
          : responseData

        const rawItems = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.results)
              ? data.results
              : data && typeof data === 'object'
                ? [data]
                : []

        return rawItems
          .filter(item => item && typeof item === 'object')
          .map(normalizeWifiConfigOption)
          .sort((left, right) => {
            if (left.active !== right.active) {
              return left.active ? -1 : 1
            }

            return String(left.essid || '').localeCompare(String(right.essid || ''))
          })
      }

      const normalizeWifiSecurityType = (value) => {
        const normalizedValue = String(value || '').trim()
        const lowerValue = normalizedValue.toLowerCase()

        if (!normalizedValue) {
          return DEFAULTS.wifiSecurityType
        }

        if (lowerValue === 'none' || lowerValue === 'open') {
          return 'NONE'
        }

        if (lowerValue.includes('wep')) {
          return 'WEP'
        }

        if (
          lowerValue.includes('wpa') ||
          lowerValue.includes('psk') ||
          lowerValue.includes('personal')
        ) {
          return 'WPA'
        }

        return normalizedValue
      }

      const clearWifiConfigFields = () => {
        form.wifiSsid = DEFAULTS.wifiSsid
        form.wifiPassword = DEFAULTS.wifiPassword
        form.wifiSecurityType = DEFAULTS.wifiSecurityType
        wifiConfigLoadedCompanyId.value = 0
        wifiConfigOptions.value = []
        selectedWifiConfigKey.value = ''
      }

      const applyWifiConfig = (wifiConfig) => {
        form.wifiSsid = normalizeValue(wifiConfig?.essid) || ''
        form.wifiPassword = wifiConfig?.password || ''
        form.wifiSecurityType = normalizeWifiSecurityType(wifiConfig?.encryption)
        wifiConfigLoadedCompanyId.value = selectedCompanyId.value
        selectedWifiConfigKey.value = wifiConfig?.selectionKey || ''
      }

      const selectWifiConfigByKey = (selectionKey) => {
        const wifiConfig = wifiConfigOptions.value.find(
          option => option.selectionKey === selectionKey,
        )

        if (wifiConfig) {
          applyWifiConfig(wifiConfig)
        }
      }

      const loadCompanyWifiConfig = async () => {
        const companyId = selectedCompanyId.value
        const requestToken = wifiConfigRequestToken.value + 1
        wifiConfigRequestToken.value = requestToken

        wifiConfigLoadError.value = ''

        if (!companyId) {
          clearWifiConfigFields()
          wifiConfigLoadError.value = 'Select a company to load Wi-Fi config.'
          return
        }

        isLoadingWifiConfig.value = true

        try {
          const response = await proxy.$http.get('WifiConfig/GetWifiConfigByCompany', {
            params: {
              companyId,
            },
          })

          if (requestToken !== wifiConfigRequestToken.value) {
            return
          }

          const wifiConfigs = normalizeWifiConfigResponse(response?.data)
          wifiConfigOptions.value = wifiConfigs

          if (!wifiConfigs.length || !wifiConfigs[0]?.essid) {
            clearWifiConfigFields()
            wifiConfigLoadError.value = 'No Wi-Fi config found for the selected company.'
            return
          }

          applyWifiConfig(wifiConfigs[0])
        } catch (error) {
          if (requestToken !== wifiConfigRequestToken.value) {
            return
          }

          clearWifiConfigFields()
          wifiConfigLoadError.value =
            error?.response?.data?.message ||
            'We could not load Wi-Fi config for the selected company.'
          console.error('[KioskQrProvisioning] Failed to load company Wi-Fi config', {
            selectedCompanyId: selectedCompanyId.value,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
        } finally {
          if (requestToken === wifiConfigRequestToken.value) {
            isLoadingWifiConfig.value = false
          }
        }
      }

      const reloadCompanyWifiConfig = () => {
        loadCompanyWifiConfig()
      }

      const normalizeExistingApkOption = (file) => {
        const fileName = getNestedStringValue(file) || 'Unnamed APK'
        const resolvedPathOrUrl = resolveUploadFilePath(file, fileName)
        const apkSearchSource = [
          fileName,
          resolvedPathOrUrl,
          file?.filePath,
          file?.path,
          file?.packageName,
          file?.applicationId,
          file?.appId,
          file?.bundleId,
        ]
          .filter(Boolean)
          .join(' ')

        if (!/\.apk(\b|$)/i.test(apkSearchSource)) {
          return null
        }

        if (
          !apkSearchSource.toLowerCase().includes(MYDEVICE_PACKAGE_ID) &&
          !MYDEVICE_APK_PATTERN.test(apkSearchSource)
        ) {
          return null
        }

        const downloadUrl = /^https?:\/\//i.test(resolvedPathOrUrl)
          ? resolvedPathOrUrl
          : buildDownloadUrlFromPath(resolvedPathOrUrl)

        return {
          id: file?.id || file?.deviceConfigFilePathId || downloadUrl,
          name: fileName,
          checksum: normalizeChecksumValue(file?.sha256 || ''),
          filePathLabel: /^https?:\/\//i.test(resolvedPathOrUrl)
            ? resolvedPathOrUrl
            : resolvedPathOrUrl.replace(/^\/+/, ''),
          downloadUrl,
        }
      }

      const sortExistingApkOptions = (items) =>
        [...items].sort((left, right) => left.name.localeCompare(right.name))

      const parseExistingApkVersionParts = (value) => {
        const versionMatch = String(value || '').match(/(?:^|[^a-z0-9])v?(\d+(?:\.\d+){1,3})(?:[_\s-]+(\d+))?/i)
        if (!versionMatch) {
          return []
        }

        return [
          ...versionMatch[1].split('.').map(part => Number(part) || 0),
          Number(versionMatch[2]) || 0,
        ]
      }

      const findLatestNumericToken = (value) => {
        const matches = String(value || '').match(/\d{10,}/g) || []
        return matches.sort((left, right) => {
          if (left.length !== right.length) {
            return right.length - left.length
          }
          return right.localeCompare(left)
        })[0] || ''
      }

      const compareNumberParts = (leftParts, rightParts) => {
        const length = Math.max(leftParts.length, rightParts.length)
        for (let index = 0; index < length; index += 1) {
          const diff = (leftParts[index] || 0) - (rightParts[index] || 0)
          if (diff !== 0) {
            return diff
          }
        }
        return 0
      }

      const compareLatestExistingApkOptions = (left, right) => {
        const leftText = [left?.name, left?.filePathLabel, left?.downloadUrl].filter(Boolean).join(' ')
        const rightText = [right?.name, right?.filePathLabel, right?.downloadUrl].filter(Boolean).join(' ')
        const leftToken = findLatestNumericToken(leftText)
        const rightToken = findLatestNumericToken(rightText)

        if (leftToken || rightToken) {
          if (leftToken.length !== rightToken.length) {
            return leftToken.length - rightToken.length
          }
          const tokenComparison = leftToken.localeCompare(rightToken)
          if (tokenComparison !== 0) {
            return tokenComparison
          }
        }

        const versionComparison = compareNumberParts(
          parseExistingApkVersionParts(leftText),
          parseExistingApkVersionParts(rightText),
        )
        if (versionComparison !== 0) {
          return versionComparison
        }

        return String(left?.name || '').localeCompare(String(right?.name || ''))
      }

      const latestExistingApkOption = computed(() =>
        existingApkOptions.value.reduce(
          (latest, item) =>
            !latest || compareLatestExistingApkOptions(item, latest) > 0 ? item : latest,
          null,
        ),
      )

      const displayedExistingApkOptions = computed(() => {
        if (!showOnlyLatestExistingApk.value) {
          return existingApkOptions.value
        }

        return latestExistingApkOption.value ? [latestExistingApkOption.value] : []
      })

      const normalizeConfigSearchLabel = (value) =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[_-]+/g, ' ')
          .replace(/\s+/g, ' ')

      const normalizeConfigSearchPath = (value) =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/\\/g, '/')
          .replace(/^\/+|\/+$/g, '')
          .replace(/\/+/g, '/')

      const scoreDownloadsConfig = (config) => {
        const normalizedName = normalizeConfigSearchLabel(config?.name)
        const normalizedPath = normalizeConfigSearchPath(config?.devicePath)
        const lastPathSegment = normalizedPath.split('/').filter(Boolean).pop() || ''

        let score = -1

        if (['download', 'downloads'].includes(lastPathSegment)) {
          score = Math.max(score, 100)
        }

        if (
          normalizedPath === 'download' ||
          normalizedPath === 'downloads' ||
          normalizedPath.endsWith('/download') ||
          normalizedPath.endsWith('/downloads')
        ) {
          score = Math.max(score, 95)
        }

        if (
          ['download', 'downloads', 'script download', 'script downloads', 'scripts download', 'scripts downloads']
            .includes(normalizedName)
        ) {
          score = Math.max(score, 90)
        }

        if (/\bdownloads?\b/.test(normalizedName) && /\bscripts?\b/.test(normalizedName)) {
          score = Math.max(score, 85)
        }

        if (/\bdownloads?\b/.test(normalizedName)) {
          score = Math.max(score, 75)
        }

        if (/\bdownloads?\b/.test(normalizedPath)) {
          score = Math.max(score, 70)
        }

        return score
      }

      const getDownloadsSearchCompanyIds = () => {
        const companyIds = [
          selectedCompanyId.value,
          ...selectedCompanyPath.value.map(company => company?.id).reverse(),
          baseConfigCompanyId.value,
        ]

        return [...new Set(companyIds.filter(Boolean))]
      }

      const getDeviceConfigsByCompany = async (companyId) => {
        const response = await proxy.$http.get('DeviceConfig/GetDeviceConfigsByCompany', {
          params: {
            CompanyId: companyId,
          },
        })

        return Array.isArray(response.data) ? response.data : []
      }

      const upsertExistingApkOption = (option) => {
        if (!option) {
          return
        }

        const dedupedItems = existingApkOptions.value.filter(
          item => item.id !== option.id && item.downloadUrl !== option.downloadUrl,
        )

        existingApkOptions.value = sortExistingApkOptions([option, ...dedupedItems])
        existingApkOptionsCompanyId.value = selectedCompanyId.value
        existingApkLoadError.value = ''
      }

      const buildDownloadUrlFromPath = (filePath) => {
        const resourceBaseUrl = proxy.$http.defaults.baseURL.replace('api/', 'Resources/')
        const normalizedPath = filePath
          .replace(/\\/g, '/')
          .replace(/^\/+/, '')
          .replace(/^resources\/+/i, '')

        return new URL(normalizedPath, resourceBaseUrl).toString()
      }

      const resolveUploadFilePath = (responseData, originalFileName) => {
        const candidates = [
          responseData?.data,
          responseData?.data?.path,
          responseData?.data?.filePath,
          responseData?.data?.url,
          responseData?.filePath,
          responseData?.path,
          responseData?.fileName,
          responseData?.name,
          responseData?.url,
        ]

        for (const candidate of candidates) {
          const stringValue = getNestedStringValue(candidate)
          if (!stringValue) {
            continue
          }

          if (/^https?:\/\//i.test(stringValue)) {
            return stringValue
          }

          if (/^download\//i.test(stringValue)) {
            return stringValue
          }

          if (/\.apk$/i.test(stringValue)) {
            return `Download/${stringValue.split(/[\\/]/).pop()}`
          }
        }

        return `Download/${originalFileName}`
      }

      const findDownloadsConfigMatches = async () => {
        const searchCompanyIds = getDownloadsSearchCompanyIds()
        const configResults = await Promise.all(
          searchCompanyIds.map(async (companyId) => ({
            companyId,
            configs: await getDeviceConfigsByCompany(companyId),
          })),
        )

        const configs = []
        const seenConfigIds = new Set()
        const matchedDownloadsConfigs = []

        configResults.forEach(({ companyId, configs: companyConfigs }, companyIndex) => {
          companyConfigs.forEach((config) => {
            if (!seenConfigIds.has(config.id)) {
              seenConfigIds.add(config.id)
              configs.push(config)
            }

            const score = scoreDownloadsConfig(config)
            if (score < 0) {
              return
            }

            matchedDownloadsConfigs.push({
              companyId,
              companyIndex,
              config,
              score,
            })
          })
        })

        matchedDownloadsConfigs.sort((left, right) => {
          if (right.score !== left.score) {
            return right.score - left.score
          }

          if (left.companyIndex !== right.companyIndex) {
            return left.companyIndex - right.companyIndex
          }

          return String(left.config?.name || '').localeCompare(String(right.config?.name || ''))
        })

        return {
          configs,
          downloadsConfigs: matchedDownloadsConfigs,
          downloadsConfig: matchedDownloadsConfigs[0]?.config || null,
          searchCompanyIds,
        }
      }

      const loadExistingApkOptions = async ({ force = false } = {}) => {
        if (
          !force &&
          existingApkOptionsCompanyId.value === selectedCompanyId.value &&
          (existingApkOptions.value.length || existingApkLoadError.value)
        ) {
          return existingApkOptions.value
        }

        if (!selectedCompanyId.value) {
          existingApkOptions.value = []
          existingApkLoadError.value = 'Select a company to browse uploaded APKs.'
          existingApkOptionsCompanyId.value = 0
          return []
        }

        isLoadingExistingApks.value = true
        existingApkLoadError.value = ''

        try {
          const { downloadsConfigs } = await findDownloadsConfigMatches()

          if (!downloadsConfigs.length) {
            existingApkOptions.value = []
            existingApkOptionsCompanyId.value = selectedCompanyId.value
            existingApkLoadError.value =
              'Downloads script config was not found for this company.'
            return []
          }

          const files = (
            await Promise.all(
              downloadsConfigs.map(async ({ config }) => {
                const response = await proxy.$http.get('DeviceConfig/GetFilePathsByDeviceConfigs', {
                  params: {
                    DeviceConfigId: config.id,
                  },
                })

                return Array.isArray(response.data) ? response.data : []
              }),
            )
          ).flat()
          const seenOptions = new Set()
          const apkOptions = sortExistingApkOptions(
            files
              .map(normalizeExistingApkOption)
              .filter((option) => {
                if (!option || seenOptions.has(option.id)) {
                  return false
                }

                seenOptions.add(option.id)
                return true
              }),
          )

          existingApkOptions.value = apkOptions
          existingApkOptionsCompanyId.value = selectedCompanyId.value
          existingApkLoadError.value = apkOptions.length
            ? ''
            : 'No MyDevice APK files were found in Scripts Downloads.'

          return apkOptions
        } catch (error) {
          existingApkOptions.value = []
          existingApkOptionsCompanyId.value = selectedCompanyId.value
          existingApkLoadError.value =
            'We could not load uploaded APK files right now.'
          console.error('[KioskQrProvisioning] Failed to load existing APK files', {
            selectedCompanyId: selectedCompanyId.value,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
          return []
        } finally {
          isLoadingExistingApks.value = false
        }
      }

      const openExistingApkMenu = () => {
        loadExistingApkOptions()
      }

      const reloadExistingApkOptions = () => {
        loadExistingApkOptions({ force: true })
      }

      const downloadExistingApkBlob = async (item) => {
        isDownloadingExistingApk.value = true
        isExistingApkDownloadProgressKnown.value = false
        existingApkDownloadProgress.value = 0

        try {
          const response = await proxy.$http(item.downloadUrl, {
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total) {
                isExistingApkDownloadProgressKnown.value = true
                existingApkDownloadProgress.value = Math.min(
                  100,
                  Math.round((progressEvent.loaded * 100) / progressEvent.total),
                )
              }
            },
          })

          if (!isExistingApkDownloadProgressKnown.value) {
            existingApkDownloadProgress.value = 100
          }

          return response.data
        } finally {
          isDownloadingExistingApk.value = false
        }
      }

      const selectExistingApk = async (item, { silent = false } = {}) => {
        if (!item?.downloadUrl) {
          return
        }

        form.apkDownloadUrl = item.downloadUrl
        form.apkChecksum = ''
        uploadedDownloadApkName.value = item.name
        apkUploadError.value = ''
        downloadUrlUploadError.value = ''
        downloadUrlUploadProgress.value = 0
        touchField('apkDownloadUrl')
        touchField('apkChecksum')
        existingApkMenu.value = false

        // Reuse the checksum returned by the API when present; only download
        // and hash the APK locally when no usable checksum is available.
        if (item.checksum && SHA_256_BASE64_URL_PATTERN.test(item.checksum)) {
          form.apkChecksum = item.checksum
          uploadedApk.value = { name: item.name, source: 'metadata' }
          if (!silent) {
            proxy.$toast.success(
              'Existing APK selected. The download URL and checksum were added to the form.',
              'Success',
              toastOptions,
            )
          }
          return
        }

        let checksumGenerated = false

        try {
          const existingApkBlob = await downloadExistingApkBlob(item)
          checksumGenerated = await generateChecksumFromApk(existingApkBlob, {
            silentSuccessToast: true,
            displayName: item.name,
            downloadSource: true,
          })
        } catch (error) {
          apkUploadError.value =
            'The download URL was added, but we could not download the selected APK to calculate its checksum automatically. Upload the APK locally or paste the checksum manually.'
          if (!silent) {
            proxy.$toast.error(apkUploadError.value, 'Error', toastOptions)
          }
          console.error('[KioskQrProvisioning] Failed to download existing APK for checksum', {
            selectedCompanyId: selectedCompanyId.value,
            item,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
          return
        }

        if (!checksumGenerated) {
          return
        }

        if (!silent) {
          proxy.$toast.success(
            'Existing APK selected. The download URL and checksum were added to the form.',
            'Success',
            toastOptions,
          )
        }
      }

      // On load, pre-fill the download URL (and checksum) with the latest APK
      // found in Scripts Downloads so the operator does not have to pick it
      // manually. Never overwrite a URL the operator has already entered.
      const autoApplyLatestExistingApk = async () => {
        if (!selectedCompanyId.value || normalizeValue(form.apkDownloadUrl)) {
          return
        }

        const options = await loadExistingApkOptions()

        if (normalizeValue(form.apkDownloadUrl)) {
          return
        }

        const latest = latestExistingApkOption.value

        if (options.length && latest) {
          await selectExistingApk(latest, { silent: true })
        }
      }

      const uploadApkForDownloadUrl = async (file, options = {}) => {
        uploadedDownloadApkName.value = ''
        downloadUrlUploadError.value = ''
        downloadUrlUploadProgress.value = 0
        touchField('apkDownloadUrl')

        if (!file) {
          return
        }

        if (!selectedCompanyId.value) {
          downloadUrlUploadError.value =
            'No company is selected, so the APK cannot be uploaded to Scripts Downloads.'
          proxy.$toast.error(downloadUrlUploadError.value, 'Error', toastOptions)
          console.error('[KioskQrProvisioning] Upload failed', {
            stage: 'precheck',
            selectedCompanyId: selectedCompanyId.value,
            fileName: file.name,
          })
          return
        }

        isUploadingDownloadUrl.value = true

        try {
          const { configs, downloadsConfigs, downloadsConfig, searchCompanyIds } = await findDownloadsConfigMatches()
          const uploadConfigType = 1

          if (!downloadsConfigs.length || !downloadsConfig) {
            downloadUrlUploadError.value =
              'Downloads script config was not found for the selected company.'
            proxy.$toast.error(downloadUrlUploadError.value, 'Error', toastOptions)
            console.error('[KioskQrProvisioning] Downloads config not found', {
              selectedCompanyId: selectedCompanyId.value,
              searchedCompanyIds: searchCompanyIds,
              configs,
            })
            return
          }

          const formData = new FormData()
          formData.append('name', file.name)
          formData.append('configType', uploadConfigType)
          formData.append('deviceConfigId', downloadsConfig.id)
          formData.append('companyId', downloadsConfig.companyId || selectedCompanyId.value)
          formData.append('file', file)

          const response = await proxy.$http.post('DeviceConfig/AddFilesToConfig', formData, {
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                downloadUrlUploadProgress.value = Math.min(
                  100,
                  Math.round((progressEvent.loaded * 100) / progressEvent.total),
                )
              }
            },
          })
          const responseData = response?.data || {}
          const isSuccess = responseData?.success === true

          if (!isSuccess) {
            downloadUrlUploadError.value =
              responseData?.message ||
              'The Scripts upload API rejected the APK for download URL generation.'
            proxy.$toast.error(downloadUrlUploadError.value, 'Error', toastOptions)
            console.error('[KioskQrProvisioning] Scripts upload API rejected file', {
              selectedCompanyId: selectedCompanyId.value,
              downloadsConfig,
              responseStatus: response?.status,
              responseData,
            })
            return
          }

          const uploadedPathOrUrl = resolveUploadFilePath(responseData, file.name)
          const finalDownloadUrl = /^https?:\/\//i.test(uploadedPathOrUrl)
            ? uploadedPathOrUrl
            : buildDownloadUrlFromPath(uploadedPathOrUrl)
          downloadUrlUploadProgress.value = 100
          form.apkDownloadUrl = finalDownloadUrl
          uploadedDownloadApkName.value = file.name
          upsertExistingApkOption(
            normalizeExistingApkOption({
              id: responseData?.data?.id || responseData?.data?.deviceConfigFilePathId,
              name: file.name,
              filePath: uploadedPathOrUrl,
              url: finalDownloadUrl,
            }),
          )
          proxy.$toast.success(
            options.successMessage ||
              'APK uploaded successfully and the download URL was added to the form.',
            'Success',
            toastOptions,
          )
        } catch (error) {
          downloadUrlUploadError.value =
            error?.response?.data?.message ||
            error?.message ||
            'We could not upload the APK for the download URL field.'
          proxy.$toast.error(downloadUrlUploadError.value, 'Error', toastOptions)
          console.error('[KioskQrProvisioning] Upload exception', {
            stage: 'upload',
            selectedCompanyId: selectedCompanyId.value,
            fileName: file.name,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
        } finally {
          isUploadingDownloadUrl.value = false
        }
      }

      const generateChecksumFromApk = async (file, options = {}) => {
        uploadedApk.value = null
        apkUploadError.value = ''
        touchField('apkChecksum')

        if (!file) {
          return false
        }

        if (!window.crypto || !window.crypto.subtle) {
          apkUploadError.value =
            'This browser cannot calculate SHA-256 checksums securely. Paste the checksum manually instead.'
          proxy.$toast.error(apkUploadError.value, 'Error', toastOptions)
          return false
        }

        isCalculatingChecksum.value = true

        try {
          // Hash the APK in the browser only. The file never leaves local memory.
          const fileBuffer = await readFileAsArrayBuffer(file)
          const digest = await window.crypto.subtle.digest('SHA-256', fileBuffer)
          form.apkChecksum = arrayBufferToBase64Url(digest)
          uploadedApk.value = {
            name: options.displayName || file?.name || 'selected APK',
          }

          if (!options.silentSuccessToast) {
            proxy.$toast.success(
              options.downloadSource
                ? 'APK downloaded and its checksum was calculated locally in the browser.'
                : 'APK checksum calculated locally and converted to URL-safe Base64.',
              'Success',
              toastOptions,
            )
          }

          return true
        } catch (error) {
          apkUploadError.value =
            'We could not read the APK file. Try again or paste the checksum manually.'
          proxy.$toast.error(apkUploadError.value, 'Error', toastOptions)
          return false
        } finally {
          isCalculatingChecksum.value = false
        }
      }

      const handleProvisioningApkUpload = async (file) => {
        if (!file) {
          return
        }

        const checksumGenerated = await generateChecksumFromApk(file, {
          silentSuccessToast: true,
        })

        if (!checksumGenerated) {
          return
        }

        await uploadApkForDownloadUrl(file, {
          successMessage:
            'APK processed successfully. The checksum and download URL were added to the form.',
        })
      }

      const onProvisioningApkFileSelected = (event) => {
        const files = event?.target?.files
        const file = files && files.length ? files[0] : null
        handleProvisioningApkUpload(file)

        if (event?.target) {
          event.target.value = ''
        }
      }

      const toggleWifiPassword = () => {
        showWifiPassword.value = !showWifiPassword.value
      }

      const ensureProvisioningReady = () => {
        touchRequiredFields()

        if (canGenerateProvisioning.value) {
          return true
        }

        proxy.$toast.error(
          'Please complete the required provisioning fields before continuing.',
          'Error',
          toastOptions,
        )
        return false
      }

      const clearQrProvisioningRecord = () => {
        lastRecordedQrSignature.value = ''
        qrProvisioningId.value = ''
      }

      const ensureQrProvisioningRecord = async () => {
        const companyId = selectedCompanyId.value
        const deviceName = normalizeVehicleInput(form.deviceImei)
        const deviceCode = normalizeValue(form.deviceMacAddress)

        if (!companyId || !deviceName || !deviceCode) {
          return false
        }

        const qrRecordId = await recordQrProvisioning()

        if (!qrRecordId) {
          proxy.$toast.error(
            'Could not create the QR provisioning record.',
            'Error',
            toastOptions,
          )
        }

        return !!qrRecordId
      }

      watch(selectedCompanyId, () => {
        clearQrProvisioningRecord()
        existingApkMenu.value = false
        existingApkOptions.value = []
        existingApkLoadError.value = ''
        existingApkOptionsCompanyId.value = 0
        showOnlyLatestExistingApk.value = true
        isDownloadingExistingApk.value = false
        isExistingApkDownloadProgressKnown.value = false
        existingApkDownloadProgress.value = 0
        companyDeviceRows.value = []
        companyDeviceLoadError.value = ''
        companyDevicesLoadedCompanyId.value = 0
        vehicleSearchInput.value = ''
        selectedExistingVehicleName.value = ''
        generatedVehicleName.value = ''
        form.apkDownloadUrl = ''
        form.apkChecksum = ''
        uploadedApk.value = null
        uploadedDownloadApkName.value = ''
        loadCompanyWifiConfig()
        loadCompanyDevices()
        autoApplyLatestExistingApk()
      }, { immediate: true })

      // The QR id is part of the admin extras bundle, so create the backend
      // record before rendering/exporting the final QR payload.
      const recordQrProvisioning = async () => {
        const companyId = selectedCompanyId.value
        const deviceName = normalizeVehicleInput(form.deviceImei)
        const deviceCode = normalizeValue(form.deviceMacAddress)

        if (!companyId || !deviceName || !deviceCode) {
          return ''
        }

        const signature = qrProvisioningSignature.value
        if (signature === lastRecordedQrSignature.value && qrProvisioningId.value) {
          return qrProvisioningId.value
        }
        lastRecordedQrSignature.value = signature

        try {
          const response = await proxy.$http.post('QrProvisioning', {
            companyId,
            deviceName,
            deviceCode,
          })
          qrProvisioningId.value = response?.data?.data?.id || response?.data?.id || ''
          return qrProvisioningId.value
        } catch (error) {
          // Clear the cache so the next export can retry the recording.
          lastRecordedQrSignature.value = ''
          qrProvisioningId.value = ''
          console.error('[KioskQrProvisioning] Failed to record QR provisioning', {
            selectedCompanyId: selectedCompanyId.value,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status,
            errorMessage: error?.message,
          })
          return ''
        }
      }

      const copyJson = async () => {
        if (!canGenerateProvisioning.value && !(await ensureQrProvisioningRecord())) {
          return
        }

        if (!ensureProvisioningReady()) {
          return
        }

        try {
          await copyTextToClipboard(formattedJson.value)
          proxy.$toast.success(
            'Provisioning JSON copied to the clipboard.',
            'Success',
            toastOptions,
          )
        } catch (error) {
          proxy.$toast.error(
            'We could not copy the JSON automatically.',
            'Error',
            toastOptions,
          )
        }
      }

      const downloadQrCode = async () => {
        if (!canGenerateProvisioning.value && !(await ensureQrProvisioningRecord())) {
          return
        }

        if (!ensureProvisioningReady()) {
          return
        }

        await nextTick()

        const qrCanvas = qrCanvasWrapper.value
          ? qrCanvasWrapper.value.querySelector('canvas')
          : null

        if (!qrCanvas) {
          proxy.$toast.error(
            'The QR code is not ready to download yet.',
            'Error',
            toastOptions,
          )
          return
        }

        const downloadLink = document.createElement('a')
        downloadLink.href = qrCanvas.toDataURL('image/png')
        downloadLink.download = 'android-mdm-kiosk-provisioning-qr.png'
        downloadLink.click()
      }

      return {
        apkUploadError,
        apkProvisioningFileInput,
        canGenerateProvisioning,
        checksumSourceMessage,
        copyJson,
        displayedExistingApkOptions,
        existingApkLoadError,
        existingApkMenu,
        existingApkOptions,
        downloadUrlSourceMessage,
        downloadQrCode,
        downloadUrlUploadError,
        downloadUrlUploadProgress,
        existingApkDownloadProgress,
        applyVehicleMacForCurrentInput,
        fieldErrors,
        filterVehicleNumberOption,
        form,
        hasMultipleWifiConfigs,
        hasWifiConfiguration,
        isLoadingCompanyDevices,
        isCalculatingChecksum,
        isDownloadingExistingApk,
        isExistingApkDownloadProgressKnown,
        isLoadingExistingApks,
        isLoadingWifiConfig,
        isProvisioningApkBusy,
        isUploadingDownloadUrl,
        normalizeChecksumField,
        onDeviceMacAddressChanged,
        onVehicleNumberChanged,
        onProvisioningApkFileSelected,
        openExistingApkMenu,
        openProvisioningApkFilePicker,
        provisioningUploadButtonText,
        qrCanvasWrapper,
        qrValue,
        regenerateDeviceMacAddress,
        reloadCompanyWifiConfig,
        reloadExistingApkOptions,
        selectExistingApk,
        selectedCompanyCode,
        selectedWifiConfigKey,
        selectWifiConfigByKey,
        showOnlyLatestExistingApk,
        showWifiPassword,
        touchField,
        toggleWifiPassword,
        validationSummary,
        validationSummaryText,
        vehicleNumberOptions,
        vehicleSearchInput,
        selectedVehicleMacAddress,
        deviceMacAddressHint,
        wifiConfigLoadError,
        wifiConfigSelectItems,
        wifiConfigStatusMessage,
        wifiSecurityTypes: WIFI_SECURITY_TYPES,
      }
    },
  })
</script>

<style scoped>
  .kiosk-shell {
    overflow: visible;
  }

  .kiosk-shell ::v-deep .v-card__title {
    padding-bottom: 10px;
    position: relative;
    z-index: 2;
    overflow: visible;
  }

  .kiosk-shell ::v-deep .v-card--material__sheet {
    position: relative;
    z-index: 3;
  }

  .kiosk-shell__body {
    padding-top: 12px;
  }

  .provisioning-panel {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .section-card {
    border-color: rgba(0, 0, 0, 0.08) !important;
    border-radius: 16px;
  }

  .section-card__title {
    padding: 18px 22px 14px !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .section-card__title-content {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .section-card__title-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: auto;
    gap: 10px;
    flex-wrap: wrap;
  }

  .section-card__body {
    padding: 20px 22px 22px !important;
  }

  .section-upload-btn {
    min-width: 220px;
    min-height: 48px;
    justify-content: center;
    white-space: nowrap;
  }

  .section-select-btn {
    min-width: 172px;
    min-height: 48px !important;
    white-space: nowrap;
  }

  .wifi-section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .section-card__body--qr {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .provisioning-note,
  .qr-warning {
    border-radius: 12px;
  }

  .qr-warning {
    margin-bottom: 12px !important;
  }

  .qr-warning ::v-deep .v-alert__wrapper {
    align-items: center;
  }

  .qr-warning ::v-deep .v-alert__content {
    font-size: 0.95rem;
    line-height: 1.35;
  }

  .qr-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 auto;
    min-height: 520px;
    padding: 20px;
    border-radius: 8px;
    background: #fafafa;
  }

  .qr-preview--empty {
    border: 1px dashed rgba(0, 0, 0, 0.12);
  }

  .qr-preview ::v-deep canvas {
    display: block;
    max-width: 100%;
    height: auto !important;
  }

  .qr-preview__frame {
    padding: 28px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  }

  .qr-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 20px;
  }

  .qr-action {
    width: 100%;
    margin: 0 !important;
    justify-content: center;
  }

  @media (max-width: 1263px) {
    .section-card--qr {
      margin-top: 4px;
    }
  }

  @media (max-width: 960px) {
    /* Trim verbose descriptions that eat vertical space on phones. */
    .provisioning-note {
      display: none !important;
    }

    .section-card__subtext {
      display: none;
    }

    .kiosk-shell ::v-deep .v-card--material__title .text-subtitle-1 {
      display: none;
    }

    .section-card__title {
      padding: 16px 18px 12px !important;
    }

    .section-card__title-content {
      align-items: stretch;
      flex-direction: column;
    }

    .section-card__title-actions {
      width: 100%;
      justify-content: stretch;
      margin-left: 0;
      flex-direction: column;
    }

    .section-card__body {
      padding: 16px 18px 18px !important;
    }

    .wifi-section-heading {
      align-items: flex-start;
    }

    .section-upload-btn {
      width: 100%;
    }

    .section-select-btn {
      width: 100%;
    }

    .qr-preview {
      min-height: 360px;
      padding: 16px;
    }

    .qr-preview__frame {
      padding: 20px;
    }

    .qr-actions {
      grid-template-columns: 1fr;
    }
  }
</style>
